import { useCallback, useState } from 'react';

import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsEnvelope, BsGeoAlt, BsTrash, BsArrowRepeat, BsPeople, BsUpload } from 'react-icons/bs';

import CrmImportWizard from '#crmFeature/components/import/CrmImportWizard';
import Button from '#globalComponents/Button/Button';
import IconButton from '#globalComponents/IconButton/IconButton';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import Tabs from '#globalComponents/Tabs/Tabs';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { useUrlFilters } from '#globalHooks/useUrlFilters';

import ContactListCompaniesTab from '../../components/ContactListCompaniesTab/ContactListCompaniesTab';
import ContactListContactsTab from '../../components/ContactListContactsTab/ContactListContactsTab';
import ContactListCrmSyncModal from '../../components/ContactListCrmSyncModal/ContactListCrmSyncModal';
import ContactListSearchOverlay from '../../components/ContactListSearchOverlay/ContactListSearchOverlay';
import ContactListStatsCard from '../../components/ContactListStatsCard/ContactListStatsCard';
import { LIST_RECIPIENT_FILTER_SCHEMA } from '../../constants/tableFilters';
import { useDeleteContactList } from '../../models/mutations';
import { useContactListDetail, useContactListContacts, useContactListCompanies } from '../../models/queries';

const TABS = { contacts: 'contacts', companies: 'companies' };

const buildStats = (label, stats) => [
	{ label, value: stats?.total ?? 0 },
	{ label: 'Mit E-Mail', value: stats?.withEmail ?? 0, icon: BsEnvelope, iconClass: 'text-primary-500' },
	{ label: 'Mit Adresse', value: stats?.withAddress ?? 0, icon: BsGeoAlt, iconClass: 'text-success-500' },
];

const CRM_SYNC_HINT = 'Alle Kontakte und Firmen dieser Liste automatisch als Deals ins CRM übernehmen';

const CrmSyncButton = ({ isActive, onClick }) =>
	isActive ? (
		<button
			type="button"
			onClick={onClick}
			title={`${CRM_SYNC_HINT} – aktiv, klicken zum Bearbeiten`}
			className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-control border-[1.5px] border-success-500/40 bg-success-200 px-3 text-sm font-medium text-success-700 transition-colors hover:border-success-500 focus:outline-none focus:ring-2 focus:ring-success-500/40"
		>
			<BsArrowRepeat className="h-3.5 w-3.5" />
			CRM-Sync aktiv
		</button>
	) : (
		<Button variant="outline" size="sm" onClick={onClick} title={CRM_SYNC_HINT}>
			<BsArrowRepeat className="h-3.5 w-3.5" />
			CRM-Sync einrichten
		</Button>
	);

CrmSyncButton.propTypes = {
	isActive: PropTypes.bool.isRequired,
	onClick: PropTypes.func.isRequired,
};

const ContactListDetailPage = () => {
	const { listId } = useParams();
	const navigate = useNavigate();

	const { data: list, isLoading: loadingList } = useContactListDetail(listId);
	const contactsFilters = useUrlFilters(LIST_RECIPIENT_FILTER_SCHEMA, { namespace: TABS.contacts });
	const companiesFilters = useUrlFilters(LIST_RECIPIENT_FILTER_SCHEMA, { namespace: TABS.companies });
	const contactsQuery = useContactListContacts(listId, contactsFilters);
	const companiesQuery = useContactListCompanies(listId, companiesFilters);
	const contactStats = contactsQuery.data?.stats;
	const companyStats = companiesQuery.data?.stats;

	const { hasCapability: hasCrm } = useCapabilityCheck('crm.access');
	const deleteListMutation = useDeleteContactList();

	// Tab + highlight live in the URL so search hits can deep-link into a list.
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTab = searchParams.get('tab') === TABS.companies ? TABS.companies : TABS.contacts;
	const highlightId = searchParams.get('highlight');

	const setActiveTab = useCallback(
		(tab) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (tab === TABS.contacts) next.delete('tab');
					else next.set('tab', tab);
					next.delete('highlight');
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	const clearHighlight = useCallback(() => {
		setSearchParams(
			(prev) => {
				if (!prev.has('highlight')) return prev;
				const next = new URLSearchParams(prev);
				next.delete('highlight');
				return next;
			},
			{ replace: true },
		);
	}, [setSearchParams]);

	const [deleteOpen, setDeleteOpen] = useState(false);
	const [showSyncModal, setShowSyncModal] = useState(false);
	const [showImport, setShowImport] = useState(false);

	const handleDeleteList = async () => {
		await deleteListMutation.mutateAsync(listId);
		navigate('/contact-lists', { replace: true });
	};

	if (loadingList) {
		return (
			<Page width="full">
				<div className="flex items-center justify-center py-24">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
				</div>
			</Page>
		);
	}

	if (!list) {
		return (
			<Page width="full">
				<div className="flex flex-col items-center justify-center py-24 gap-3">
					<BsPeople className="w-12 h-12 text-bgBase-800" />
					<h2 className="text-lg font-semibold text-text">Kontaktliste nicht gefunden</h2>
					<button
						onClick={() => navigate('/contact-lists')}
						className="text-primary-600 hover:text-primary-700 text-sm font-medium"
					>
						Zur Übersicht
					</button>
				</div>
			</Page>
		);
	}

	return (
		<Page
			width="full"
			scrollMode="content"
			className="overflow-x-hidden"
			header={
				<PageHeader breadcrumbs={[{ label: 'Kontaktlisten', to: '/contact-lists' }, list.name]}>
					<ContactListSearchOverlay />
					<div className="flex items-center gap-2">
						{hasCrm && (
							<Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
								<BsUpload className="h-3.5 w-3.5" />
								CSV importieren
							</Button>
						)}
						{hasCrm && (
							<CrmSyncButton
								isActive={Boolean(list.crmSyncPipelineId)}
								onClick={() => setShowSyncModal(true)}
							/>
						)}
						<IconButton
							icon={BsTrash}
							label="Liste löschen"
							tone="danger"
							onClick={() => setDeleteOpen(true)}
						/>
					</div>
				</PageHeader>
			}
		>
			<ContactListStatsCard
				list={list}
				stats={
					activeTab === TABS.contacts
						? buildStats('Kontakte', contactStats)
						: buildStats('Firmen', companyStats)
				}
			/>

			<Tabs
				tabs={[
					{ id: TABS.contacts, label: 'Kontakte', count: contactStats?.total ?? 0 },
					{ id: TABS.companies, label: 'Firmen', count: companyStats?.total ?? 0 },
				]}
				activeId={activeTab}
				onChange={setActiveTab}
				className="mb-3"
			/>

			<div className="flex-1 min-h-0 min-w-0 flex flex-col">
				{activeTab === TABS.contacts ? (
					<ContactListContactsTab
						listId={listId}
						hasCrm={hasCrm}
						urlFilters={contactsFilters}
						query={contactsQuery}
						highlightId={highlightId}
						onHighlightConsumed={clearHighlight}
					/>
				) : (
					<ContactListCompaniesTab
						listId={listId}
						hasCrm={hasCrm}
						urlFilters={companiesFilters}
						query={companiesQuery}
						highlightId={highlightId}
						onHighlightConsumed={clearHighlight}
					/>
				)}
			</div>

			<ConfirmModal
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={handleDeleteList}
				title="Kontaktliste löschen"
				message={`Möchten Sie die Kontaktliste "${list.name}" wirklich löschen? Alle Verknüpfungen zu Kontakten werden entfernt.`}
				confirmText="Liste löschen"
				cancelText="Abbrechen"
				isLoading={deleteListMutation.isPending}
			/>

			{hasCrm && (
				<ContactListCrmSyncModal isOpen={showSyncModal} onClose={() => setShowSyncModal(false)} list={list} />
			)}

			{hasCrm && showImport && <CrmImportWizard isOpen onClose={() => setShowImport(false)} contactList={list} />}
		</Page>
	);
};

export default ContactListDetailPage;
